/* ═══════════════════════════════════════════════════════════════════════════
   Route map
   ───────────────────────────────────────────────────────────────────────────
   Every path the HTTP client can request, in one file, as functions rather
   than as strings scattered through `http.ts`.

   Written down separately for two reasons. The first is that this file *is*
   the request half of the backend specification — `docs/API.md` is generated
   from the same list, and a Go developer can read it without reading the
   client. The second is `encodeURIComponent`: a provider slug or a promo code
   interpolated raw into a template literal works for every id in the seed data
   and breaks on the first one containing a space or a slash, which is the kind
   of bug that reaches production because nobody's test data has a slash in it.

   Paths are relative to `config.apiUrl` and always start with `/`.
   Query strings are not built here — the client takes params as an object so
   it can drop the undefined ones, which is what keeps `?area=undefined` out of
   the logs.
   ═══════════════════════════════════════════════════════════════════════════ */

const e = encodeURIComponent

export const routes = {
  /* ── catalog: public, cacheable, no credential ─────────────────────────── */
  categories: () => '/categories',
  areas: () => '/areas',
  cities: () => '/cities',
  providers: () => '/providers',
  provider: (slug: string) => `/providers/${e(slug)}`,
  providerReviews: (providerId: string) => `/providers/${e(providerId)}/reviews`,
  featuredReviews: () => '/reviews/featured',
  /* Keyed by list, not by provider: a category id addresses HexClean's own
     price list and a provider id addresses a partner's. One route serves both
     because `listId` already carries the distinction.
     The list *is* the resource, so it is addressed directly rather than
     through a `/menu` sub-path — there is nothing else at `/lists/{id}` for
     the suffix to distinguish it from. */
  menu: (listId: string) => `/lists/${e(listId)}`,
  slots: (listId: string) => `/lists/${e(listId)}/slots`,
  search: () => '/search',
  promos: () => '/promos',
  /* Pricing, and the promo check that used to live at `/promos/validate` — the
     cap, the VAT, the delivery waivers and the redemption limits are one
     calculation, so there is one route that performs it. Not under `/orders`:
     a quote places nothing and is answered for a signed-out visitor. */
  quote: () => '/quote',
  /* A hold is its own resource, not a sub-path of the list it reserves from:
     it outlives the request that created it and is released by id, and the id
     is the only thing the customer's checkout screen still holds by then. */
  slotHold: () => '/slots/hold',
  slotHoldById: (holdId: string) => `/slots/hold/${e(holdId)}`,

  /* ── auth ──────────────────────────────────────────────────────────────── */
  /* One route, three sign-in methods. The provider runs the challenge and the
     API verifies the token it minted — there is no code for us to check. */
  signIn: () => '/auth/firebase',
  /* No body and no bearer token: the credential is the HttpOnly refresh
     cookie, which the browser attaches and script cannot read. */
  refresh: () => '/auth/refresh',
  signOut: () => '/auth/logout',
  me: () => '/me',
  /* One path, two verbs, and the device token is in the body on both — it is
     a credential for pushing to somebody's phone, and a path segment ends up
     in access logs and proxy caches. */
  devices: () => '/devices',

  /* ── the signed-in customer ──────────────────────────────────────────────
     No `/me` prefix on any of these. The scope of an address or a saved shop
     is the token that asked for it, and a path segment repeating what the
     credential already says is a segment that can disagree with it. */
  addresses: () => '/addresses',
  address: (id: string) => `/addresses/${e(id)}`,
  favourites: () => '/favourites',
  favourite: (providerId: string) => `/favourites/${e(providerId)}`,

  /* ── orders ────────────────────────────────────────────────────────────── */
  orders: () => '/orders',
  order: (id: string) => `/orders/${e(id)}`,
  cancelOrder: (id: string) => `/orders/${e(id)}/cancel`,
  rateOrder: (id: string) => `/orders/${e(id)}/rating`,
  /* No `advanceOrder`. The tracking screen's simulated courier is a mock
     affordance and stays one — a customer who can advance their own order can
     mark their own delivery complete, which is what decides when money settles
     and when the cancellation window shuts. See the contract. */

  /* ── supply: joining ───────────────────────────────────────────────────── */
  /* The Join form, and nothing else. The eighteen operator paths that used to
     follow — the application pipeline, document review, activation, partner
     staff and payout details — were dead in this app and live in
     `hexclean-admin`. Removed in Phase 1.

     Not under `/partner`: that prefix is the partner app's authenticated
     surface, and an application is submitted by someone who is not a partner
     yet and holds no token. */
  applications: () => '/applications',
} as const
